import axios from 'axios';
import {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  ChangePasswordRequest,
  User,
  UserProfile,
} from '../types';
import { logger } from '../utils/logger';
import { deduplicateRequest, clearRequestCache } from '../utils/requestCache';
import { triggerSessionEnd } from '../session/graceful-exit';

// Validate required environment variables
if (!process.env.REACT_APP_API_URL) {
  logger.critical('REACT_APP_API_URL environment variable is not set!');
  throw new Error('Missing required environment variable: REACT_APP_API_URL');
}

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include access token
authApi.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh promise to prevent duplicate refresh requests
let refreshTokenPromise: Promise<AuthResponse> | null = null;

// Add response interceptor to handle token refresh
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle rate limiting separately from authentication errors
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'] ||
                        error.response.headers['ratelimit-reset'];

      logger.warn('Rate limit exceeded', { retryAfter, url: originalRequest.url });

      return Promise.reject({
        ...error,
        message: 'Rate limit exceeded. Please slow down.',
        retryAfter,
        isRateLimit: true,
      });
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          // Deduplicate refresh token requests
          if (!refreshTokenPromise) {
            refreshTokenPromise = axios
              .post<AuthResponse>(`${API_BASE_URL}/auth/refresh`, {
                refreshToken: tokens.refreshToken,
              })
              .then(response => response.data)
              .finally(() => {
                refreshTokenPromise = null;
              });
          }

          const authResponse = await refreshTokenPromise;
          const { tokens: newTokens } = authResponse;
          saveTokens(newTokens);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return authApi(originalRequest);
        }
      } catch (refreshError: any) {
        // If refresh also fails with 429, don't redirect
        if (refreshError?.response?.status === 429) {
          logger.warn('Rate limit during token refresh');
          return Promise.reject({
            message: 'Rate limit exceeded during token refresh',
            status: 429,
            isRateLimit: true,
          });
        }

        // Refresh failed → graceful exit (save/stash → calm logout).
        clearTokens();
        clearRequestCache();
        if (!triggerSessionEnd() && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Token storage helpers
const TOKEN_STORAGE_KEY = 'cnf_auth_tokens';

export function saveTokens(tokens: { accessToken: string; refreshToken: string }): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

export function getTokens(): { accessToken: string; refreshToken: string } | null {
  const tokensStr = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!tokensStr) return null;
  try {
    return JSON.parse(tokensStr);
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  clearRequestCache(); // Clear in-flight requests when clearing tokens
}

// Auth API methods

/**
 * Login with email and password
 */
export async function login(credentials: LoginRequest): Promise<AuthResponse> {
  const response = await authApi.post<AuthResponse>('/auth/login', credentials);
  const { tokens } = response.data;
  saveTokens(tokens);
  return response.data;
}

/**
 * Register a new user (customer self-registration)
 */
export async function register(userData: RegisterRequest): Promise<AuthResponse> {
  const response = await authApi.post<AuthResponse>('/auth/register', userData);
  const { tokens } = response.data;
  saveTokens(tokens);
  return response.data;
}

/**
 * Logout (invalidate refresh token)
 */
export async function logout(): Promise<void> {
  const tokens = getTokens();
  if (tokens?.refreshToken) {
    try {
      await authApi.post('/auth/logout', { refreshToken: tokens.refreshToken });
    } catch (error) {
      logger.error('Logout error:', error);
    }
  }
  clearTokens();
}

/**
 * Logout all sessions
 */
export async function logoutAll(): Promise<void> {
  await authApi.post('/auth/logout-all');
  clearTokens();
}

/**
 * Refresh access token
 */
export async function refreshAccessToken(): Promise<AuthResponse> {
  const tokens = getTokens();
  if (!tokens?.refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await authApi.post<AuthResponse>('/auth/refresh', {
    refreshToken: tokens.refreshToken,
  });

  const { tokens: newTokens } = response.data;
  saveTokens(newTokens);
  return response.data;
}

/**
 * Get current user info (with request deduplication)
 */
export async function getCurrentUser(): Promise<User & { profile?: UserProfile }> {
  return deduplicateRequest('auth:getCurrentUser', async () => {
    const response = await authApi.get<User & { profile?: UserProfile }>('/auth/me');
    return response.data;
  });
}

/**
 * Change password
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  await authApi.post('/auth/change-password', data);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const tokens = getTokens();
  return !!tokens?.accessToken;
}

// User Management API (Admin)

/**
 * Get all users (owner only)
 */
export async function getAllUsers(filters?: { role?: string; status?: string }): Promise<User[]> {
  const response = await authApi.get<User[]>('/users', { params: filters });
  return response.data;
}

/**
 * Get user by ID
 */
export async function getUserById(userId: number): Promise<User & { profile?: UserProfile }> {
  const response = await authApi.get<User & { profile?: UserProfile }>(`/users/${userId}`);
  return response.data;
}

/**
 * Create user (owner only - for creating staff accounts)
 */
export async function createUser(userData: RegisterRequest): Promise<User> {
  const response = await authApi.post<User>('/users', userData);
  return response.data;
}

/**
 * Update user
 */
export async function updateUser(userId: number, userData: Partial<User>): Promise<User> {
  const response = await authApi.put<User>(`/users/${userId}`, userData);
  return response.data;
}

/**
 * Delete user (owner only)
 */
export async function deleteUser(userId: number): Promise<void> {
  await authApi.delete(`/users/${userId}`);
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId: number, profileData: Partial<UserProfile>): Promise<UserProfile> {
  const response = await authApi.put<UserProfile>(`/users/${userId}/profile`, profileData);
  return response.data;
}

/**
 * Reset user password (owner only)
 */
export async function resetUserPassword(userId: number, newPassword: string): Promise<void> {
  await authApi.post(`/users/${userId}/reset-password`, { newPassword });
}

export default authApi;
